import { clientListSchema } from '@fieldops/shared'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { NewClientDialog } from '../components/client/ClientDialogs.js'
import { SortMenu } from '../components/ListControls.js'
import { Icon } from '../components/Icon.js'
import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  EmptyState,
  ErrorState,
  FilterChip,
  ListHead,
  Page,
  PageHeader,
  Pagination,
  SearchBox,
  Segmented,
  SkeletonRows,
  Toolbar,
  humanise,
  listRowClass,
} from '../components/ui.js'
import { cn } from '../lib/cn.js'
import { useSession } from '../lib/session.js'
import { errorMessage, useTRPC, type RouterOutputs } from '../lib/trpc.js'
import { useUrlState } from '../lib/url-state.js'

/**
 * Every client, as a list to search.
 *
 * A row answers the two things the office looks a client up for — who to ring
 * and where they are — without opening the record: the primary contact and
 * their number, and the billing suburb. "New client" searches before it
 * creates, because the caller is often already on the books (decision 60).
 */

type ClientRow = RouterOutputs['clients']['list']['items'][number]

const SORT_FIELDS = [
  { key: 'name', label: 'Name' },
  { key: 'number', label: 'Client number' },
  { key: 'createdAt', label: 'Newest' },
] as const

const TYPES = ['RESIDENTIAL', 'COMMERCIAL', 'STRATA', 'GOVERNMENT', 'REAL_ESTATE'] as const
type TypeFilter = 'ALL' | (typeof TYPES)[number]

const GRID = 'sm:grid-cols-[minmax(0,1.5fr)_minmax(0,1.2fr)_minmax(0,0.8fr)_7.5rem_7rem]'

export function Clients() {
  const trpc = useTRPC()
  const { can } = useSession()
  const [params, setParams] = useUrlState(clientListSchema)
  const [creating, setCreating] = useState(false)

  const { data, isPending, error, refetch } = useQuery(trpc.clients.list.queryOptions(params))

  const typeFilter: TypeFilter = params.type.length === 1 ? params.type[0]! : 'ALL'
  const filtered = Boolean(params.q) || params.type.length > 0 || params.archived

  return (
    <Page>
      <PageHeader
        title="Clients"
        description={
          data
            ? `${data.total.toLocaleString('en-AU')} ${data.total === 1 ? 'client' : 'clients'}${
                params.archived ? ', including archived' : ''
              }`
            : 'Everyone the business does work for'
        }
        actions={
          can('CLIENT_EDIT') ? (
            <Button
              variant="primary"
              className="rounded-full"
              onClick={() => setCreating(true)}
              icon={<Icon name="plus" className="size-4" />}
            >
              New client
            </Button>
          ) : null
        }
      />

      <Card>
        <Toolbar className="border-b-0">
          <SearchBox
            value={params.q ?? ''}
            onChange={(q) => setParams({ q: q || undefined, page: 1 }, { replace: true })}
            placeholder="Search name, email or phone"
            className="max-sm:w-auto max-sm:min-w-0 max-sm:flex-1"
          />
          <Segmented<TypeFilter>
            className="max-sm:order-2"
            label="Kind of client"
            value={typeFilter}
            onChange={(value) => setParams({ type: value === 'ALL' ? [] : [value], page: 1 })}
            options={[
              { value: 'ALL', label: 'All' },
              ...TYPES.map((type) => ({ value: type, label: humanise(type) })),
            ]}
          />
          <FilterChip
            className="max-sm:order-3"
            active={params.archived}
            onClick={() => setParams({ archived: !params.archived, page: 1 })}
          >
            Include archived
          </FilterChip>
          <ButtonGroup label="How the list reads" className="ml-auto rounded-lg max-sm:order-1">
            <SortMenu
              fields={SORT_FIELDS}
              sort={params.sort}
              dir={params.dir}
              onChange={(next) => setParams(next)}
            />
          </ButtonGroup>
        </Toolbar>
      </Card>

      <Card>
        {error ? (
          <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
        ) : isPending ? (
          <SkeletonRows />
        ) : data.items.length === 0 ? (
          <EmptyState
            icon="clients"
            title={filtered ? 'No clients match' : 'No clients yet'}
            description={
              filtered
                ? 'Try a shorter search or another kind of client.'
                : 'Clients arrive with the ServiceM8 import, or add one here.'
            }
            action={
              !filtered && can('CLIENT_EDIT') ? (
                <Button variant="primary" onClick={() => setCreating(true)}>
                  New client
                </Button>
              ) : null
            }
          />
        ) : (
          <>
            <ListHead className={GRID}>
              <span>Client</span>
              <span>Contact</span>
              <span>Suburb</span>
              <span>Work</span>
              <span className="text-right">Kind</span>
            </ListHead>
            <ul className="divide-y divide-ink-100">
              {data.items.map((client) => (
                <li key={client.id}>
                  <ClientListRow client={client} />
                </li>
              ))}
            </ul>
          </>
        )}

        {data ? (
          <Pagination
            page={data.page}
            pageCount={data.pageCount}
            total={data.total}
            onChange={(page) => setParams({ page })}
          />
        ) : null}
      </Card>

      <NewClientDialog open={creating} onClose={() => setCreating(false)} />
    </Page>
  )
}

function ClientListRow({ client }: { client: ClientRow }) {
  const contact = client.contacts[0]
  const contactName = contact ? [contact.firstName, contact.lastName].filter(Boolean).join(' ') : null
  const reach = contact?.mobile ?? contact?.phone ?? client.phone ?? contact?.email ?? client.email
  const suburb = client.billingAddress?.suburb ?? client.billingAddress?.city

  return (
    <Link to={`/clients/${client.id}`} className={cn(listRowClass, GRID)}>
      <span className="flex min-w-0 basis-full flex-wrap items-center gap-x-1.5 sm:basis-auto sm:flex-nowrap">
        <span
          className="min-w-0 text-sm font-medium break-words text-ink-900 sm:truncate"
          title={client.name}
        >
          {client.name}
        </span>
        <span className="shrink-0 text-[11px] tabular-nums text-ink-400">Client {client.number}</span>
        {client.isOnCreditHold ? (
          <Badge tone="red" className="shrink-0">
            Credit hold
          </Badge>
        ) : null}
        {client.archivedAt ? (
          <Badge tone="amber" className="shrink-0">
            Archived
          </Badge>
        ) : null}
      </span>

      <span className="min-w-0 text-xs text-ink-500 sm:truncate" title={[contactName, reach].filter(Boolean).join(' · ')}>
        {contactName && contactName !== client.name ? (
          <>
            <span className="font-medium text-ink-800">{contactName}</span>
            {' · '}
          </>
        ) : null}
        <span className="break-all sm:break-normal">{reach ?? 'No number or email'}</span>
      </span>

      <span className={cn('text-xs text-ink-600', !suburb && 'hidden sm:block')}>
        {suburb ?? <span className="text-ink-400">—</span>}
      </span>

      <span className="text-xs tabular-nums text-ink-500">
        {client._count.jobs} {client._count.jobs === 1 ? 'job' : 'jobs'} · {client._count.sites}{' '}
        {client._count.sites === 1 ? 'property' : 'properties'}
      </span>

      <span className="sm:text-right">
        <Badge>{humanise(client.type)}</Badge>
      </span>
    </Link>
  )
}
